"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, type MouseEvent as ReactMouseEvent, type CSSProperties } from "react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import type { Card } from "@/lib/api";
import RichDescription from "@/app/components/RichDescription";
import type { RelatedCard } from "@/app/components/RichDescription";
import { getCardDisplayModel } from "@/lib/card-display";
import { cachedFetch } from "@/lib/fetch-cache";
import LocalizedNames from "@/app/components/LocalizedNames";
import EntityUpdateHistory from "@/app/components/EntityUpdateHistory";
import RelatedCards from "@/app/components/RelatedCards";
import EntityProse from "@/app/components/EntityProse";
import EntityPairings from "@/app/components/EntityPairings";
import EntityDraftRecs from "@/app/components/EntityDraftRecs";
import EntitySummary from "@/app/components/EntitySummary";
import type { EntitySummaryData } from "@/lib/entity-summary";
import type { DraftRecs, Pairings, RelatedGroup } from "@/lib/entity-links";
import { imageUrl, fullCardUrl, enchantedCardUrl } from "@/lib/image-url";
import EntityRunStats, { type EntityStats } from "@/app/components/EntityRunStats";
import EntityVersionSelect from "@/app/components/EntityVersionSelect";
import HoverTooltip from "@/app/components/HoverTooltip";
import { useChannel, useBetaPrefix } from "@/lib/use-lang-prefix";
import BetaDiffNotice from "@/app/components/BetaDiffNotice";
import "@/app/card-revamp.css";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Per-entity character accent for the wiki page spine (--spine).
const SPINE_COLOR: Record<string, string> = {
  ironclad: "var(--color-ironclad)",
  silent: "var(--color-silent)",
  defect: "var(--color-defect)",
  necrobinder: "var(--color-necrobinder)",
  regent: "var(--color-regent)",
  colorless: "var(--color-colorless)",
  curse: "var(--color-curse)",
  status: "var(--text-muted)"
};

// Headline figures for the infobox mini-stats block. Same endpoint EntityRunStats
// fetches, so cachedFetch dedupes it (no extra request).
interface MiniBracket {
  picks: number;
  win_rate: number;
  pick_rate: number;
  score: number | null;
  elo: number | null;
}
interface MiniStats extends MiniBracket {
  brackets?: Record<string, MiniBracket>;
}
const typeIcons: Record<string, string> = {
  Attack: "\u2694",
  Skill: "\uD83D\uDEE1",
  Power: "\u2726",
  Status: "\u25C6",
  Curse: "\u2620",
  Quest: "\u2605"
};
const keywordTooltips: Record<string, string> = {
  Exhaust: "Remove this card from your deck when played.",
  Ethereal: "If this card is in your hand at end of turn, discard it.",
  Innate: "Always appears in your opening hand.",
  Unplayable: "Cannot be played from your hand.",
  Retain: "Keep this card in your hand at end of turn.",
  Sly: "Can be played from the discard pile.",
  Eternal: "Cannot be removed from your deck."
};
function buildInteractiveWords(keywords: string[], powerData: Record<string, {
  id: string;
  name: string;
  description: string;
  type: string;
  image_url: string | null;
}>, keywordData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, glossaryData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, orbData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, bp: string): Record<string, {
  tooltip: string;
  href: string;
}> {
  const words: Record<string, {
    tooltip: string;
    href: string;
  }> = {};
  // Add keyword names (Sly, Exhaust, Ethereal, etc.)
  for (const kw of keywords) {
    const data = keywordData[kw.toLowerCase()];
    const desc = data?.description || keywordTooltips[kw] || "";
    words[kw] = {
      tooltip: desc,
      href: `${bp}/keywords/${kw.toLowerCase()}`
    };
  }
  // Add power names from [gold] tagged text (Dexterity, Thorns, Block, Strength, etc.)
  for (const [name, data] of Object.entries(powerData)) {
    words[data.name] = {
      tooltip: data.description,
      href: `${bp}/powers/${data.id.toLowerCase()}`
    };
  }
  // Add glossary terms (Block, Discard Pile, Draw Pile, Fatal, Forge, etc.)
  for (const [name, data] of Object.entries(glossaryData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/keywords/${data.id.toLowerCase()}`
      };
    }
  }
  // Add orb names (Lightning, Frost, Dark, Glass, Plasma)
  for (const [name, data] of Object.entries(orbData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/orbs/${data.id.toLowerCase()}`
      };
    }
  }
  return words;
}
function InlineTooltip(t0) {
  const $ = _c(23);
  const {
    label,
    tooltip,
    href,
    color,
    image
  } = t0;
  const [show, setShow] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShow(true);
    t2 = () => setShow(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== color) {
    t3 = {
      color
    };
    $[2] = color;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <span className="font-medium">{label}</span>;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== tooltip) {
    t5 = tooltip && <span className="text-[var(--text-muted)] ml-1">{tooltip.replace(/\[.*?\]/g, "").replace(/\n/g, " ").slice(0, 80)}</span>;
    $[6] = tooltip;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== image || $[9] !== label || $[10] !== show || $[11] !== tooltip) {
    t6 = show && tooltip && <span className="absolute z-[100] bottom-full left-0 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none text-left">{image && <img src={image} alt="" className="w-6 h-6 object-contain mb-1" crossOrigin="anonymous" />}<span className="font-semibold text-xs text-[var(--text-primary)] block">{label}</span><span className="text-[10px] text-[var(--text-secondary)] leading-relaxed block mt-1"><RichDescription text={tooltip} /></span></span>;
    $[8] = image;
    $[9] = label;
    $[10] = show;
    $[11] = tooltip;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <span className="relative inline-flex items-center cursor-pointer" onMouseEnter={t1} onMouseLeave={t2} style={t3}>{t4}{t5}{t6}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const inner = t7;
  if (href) {
    let t8;
    if ($[18] !== href || $[19] !== inner) {
      t8 = <Link href={href} className="block">{inner}</Link>;
      $[18] = href;
      $[19] = inner;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  let t8;
  if ($[21] !== inner) {
    t8 = <div>{inner}</div>;
    $[21] = inner;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
const energyIconMap: Record<string, string> = {
  ironclad: "ironclad",
  silent: "silent",
  defect: "defect",
  necrobinder: "necrobinder",
  regent: "regent",
  colorless: "colorless"
};

// Merchant price ranges
function getMerchantPriceRange(rarity: string, color: string): {
  min: number;
  max: number;
} | null {
  const isColorless = color === "colorless";
  let base: number;
  switch (rarity) {
    case "Common":
      base = 50;
      break;
    case "Uncommon":
      base = 75;
      break;
    case "Rare":
      base = 150;
      break;
    default:
      return null;
  }
  if (isColorless) base = Math.round(base * 1.15);
  return {
    min: Math.floor(base * 0.95),
    max: Math.ceil(base * 1.05)
  };
}
export default function CardDetail(t0) {
  const $ = _c(456);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialCard,
    initialEnchantments,
    initialStats,
    initialPairings,
    initialRecs,
    initialRelated,
    summary: t2
  } = t1;
  const summary = t2 === undefined ? null : t2;
  const params = useParams();
  const id = params.id as string;
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  const channel = useChannel();
  const [card, setCard] = useState(initialCard ?? null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [spawnedCards, setSpawnedCards] = useState(t3);
  const [loading, setLoading] = useState(!initialCard);
  const [notFound, setNotFound] = useState(false);
  const [upgraded, setUpgraded] = useState(false);
  const [betaArt] = useState(false);
  const [cardImgFailed, setCardImgFailed] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedEnch, setSelectedEnch] = useState("none");
  const [activeSection, setActiveSection] = useState("performance");
  const [miniStats, setMiniStats] = useState(null);
  const [statsBracket, setStatsBracket] = useState("all");
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [powerData, setPowerData] = useState(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [keywordData, setKeywordData] = useState(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [glossaryData, setGlossaryData] = useState(t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {};
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [orbData, setOrbData] = useState(t7);
  let t8;
  if ($[7] !== initialEnchantments) {
    t8 = initialEnchantments ?? [];
    $[7] = initialEnchantments;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const cardEnchantments = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {};
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const [enchMeta, setEnchMeta] = useState(t9);
  let t10;
  if ($[10] !== id || $[11] !== initialCard || $[12] !== lang) {
    t10 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/cards/${id}?lang=${lang}`).then(data => {
        setCard(data);
        if (data.spawns_cards && data.spawns_cards.length > 0) {
          Promise.all(data.spawns_cards.map(sid => cachedFetch(`${API}/api/cards/${sid}?lang=${lang}`).catch(_temp))).then(results => setSpawnedCards(results.filter(Boolean) as Card[]));
        }
      }).catch(() => {
        if (!initialCard) {
          setNotFound(true);
        }
      }).finally(() => setLoading(false));
    };
    $[10] = id;
    $[11] = initialCard;
    $[12] = lang;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== id || $[15] !== lang) {
    t11 = [id, lang];
    $[14] = id;
    $[15] = lang;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  if ($[17] !== cardEnchantments.length || $[18] !== lang) {
    t12 = () => {
      cachedFetch(`${API}/api/powers?lang=${lang}`).then(powers => {
        const m = {};
        for (const p of powers) {
          m[p.name.toLowerCase()] = p;
        }
        setPowerData(m);
      });
      cachedFetch(`${API}/api/keywords?lang=${lang}`).then(kws => {
        const m_0 = {};
        for (const k of kws) {
          m_0[k.name.toLowerCase()] = k;
        }
        setKeywordData(m_0);
      });
      cachedFetch(`${API}/api/glossary?lang=${lang}`).then(terms => {
        const m_1 = {};
        for (const t_0 of terms) {
          m_1[t_0.name.toLowerCase()] = t_0;
        }
        setGlossaryData(m_1);
      });
      cachedFetch(`${API}/api/orbs?lang=${lang}`).then(orbs => {
        const m_2 = {};
        for (const o of orbs) {
          m_2[o.name.toLowerCase()] = o;
        }
        setOrbData(m_2);
      });
      if (cardEnchantments.length > 0) {
        cachedFetch(`${API}/api/enchantments?lang=${lang}`).then(enchs => {
          const m_3 = {};
          for (const e of enchs) {
            m_3[e.id.toLowerCase()] = e;
          }
          setEnchMeta(m_3);
        });
      }
    };
    $[17] = cardEnchantments.length;
    $[18] = lang;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== lang) {
    t13 = [lang];
    $[20] = lang;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[22] !== id) {
    t14 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/runs/stats/cards/${id}`).then(setMiniStats).catch(_temp2);
    };
    t15 = [id];
    $[22] = id;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  useEffect(t14, t15);
  let t16;
  if ($[25] !== card) {
    t16 = () => {
      if (!card) {
        return;
      }
      const secs = Array.from(document.querySelectorAll(".card-rvmp section[id]"));
      if (secs.length === 0) {
        return;
      }
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e_0 => {
          if (e_0.isIntersecting) {
            setActiveSection((e_0.target as HTMLElement).id);
          }
        });
      }, {
        rootMargin: "-130px 0px -70% 0px"
      });
      secs.forEach(s => obs.observe(s));
      return () => obs.disconnect();
    };
    $[25] = card;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== card || $[28] !== cardEnchantments.length) {
    t17 = [card, cardEnchantments.length];
    $[27] = card;
    $[28] = cardEnchantments.length;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  useEffect(t16, t17);
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (e_1, secId) => {
      e_1.preventDefault();
      const el = document.getElementById(secId);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
        setActiveSection(secId);
      }
    };
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const handleTocClick = t18;
  if (loading) {
    let t19;
    if ($[31] !== t) {
      t19 = t("Loading...");
      $[31] = t;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== t19) {
      t20 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="text-center py-12 text-[var(--text-muted)]">{t19}</div></div>;
      $[33] = t19;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    return t20;
  }
  if (notFound || !card) {
    const t19 = `${bp}/cards`;
    let t20;
    if ($[35] !== t) {
      t20 = t("Back to");
      $[35] = t;
      $[36] = t20;
    } else {
      t20 = $[36];
    }
    let t21;
    if ($[37] !== t) {
      t21 = t("Cards");
      $[37] = t;
      $[38] = t21;
    } else {
      t21 = $[38];
    }
    let t22;
    if ($[39] !== t19 || $[40] !== t20 || $[41] !== t21) {
      t22 = <Link href={t19} className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6">← {t20} {t21}</Link>;
      $[39] = t19;
      $[40] = t20;
      $[41] = t21;
      $[42] = t22;
    } else {
      t22 = $[42];
    }
    let t23;
    if ($[43] !== t) {
      t23 = t("Card Not Found");
      $[43] = t;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    let t24;
    if ($[45] !== t23) {
      t24 = <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t23}</h1>;
      $[45] = t23;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    let t25;
    if ($[47] !== id || $[48] !== t) {
      t25 = t("No card exists with ID \u201C{id}\u201D.", {
        id
      });
      $[47] = id;
      $[48] = t;
      $[49] = t25;
    } else {
      t25 = $[49];
    }
    let t26;
    if ($[50] !== t25) {
      t26 = <p className="text-[var(--text-muted)]">{t25}</p>;
      $[50] = t25;
      $[51] = t26;
    } else {
      t26 = $[51];
    }
    let t27;
    if ($[52] !== t24 || $[53] !== t26) {
      t27 = <div className="text-center py-12">{t24}{t26}</div>;
      $[52] = t24;
      $[53] = t26;
      $[54] = t27;
    } else {
      t27 = $[54];
    }
    let t28;
    if ($[55] !== t22 || $[56] !== t27) {
      t28 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t22}{t27}</div>;
      $[55] = t22;
      $[56] = t27;
      $[57] = t28;
    } else {
      t28 = $[57];
    }
    return t28;
  }
  let characterLabel;
  let costLabel;
  let displayKeywords;
  let displayType;
  let enchActive;
  let isUpgraded;
  let priceRange;
  let renderSrc;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let targetLabel;
  let variantImg;
  if ($[58] !== activeSection || $[59] !== betaArt || $[60] !== bp || $[61] !== card || $[62] !== cardEnchantments || $[63] !== cardImgFailed || $[64] !== channel || $[65] !== glossaryData || $[66] !== id || $[67] !== initialStats || $[68] !== keywordData || $[69] !== lang || $[70] !== orbData || $[71] !== powerData || $[72] !== selectedEnch || $[73] !== selectedVariant || $[74] !== spawnedCards || $[75] !== statsBracket || $[76] !== summary || $[77] !== t || $[78] !== upgraded) {
    const display = getCardDisplayModel(card, upgraded);
    const activeVariant = selectedVariant && card.type_variants ? card.type_variants[selectedVariant] : null;
    activeVariant ? activeVariant.damage : display.damage;
    activeVariant ? activeVariant.block : display.block;
    activeVariant ? card.hit_count : display.hitCount;
    const cost = activeVariant ? card.cost : display.cost;
    displayType = activeVariant ? activeVariant.type : card.type;
    isUpgraded = display.isUpgraded;
    const hasUpgrade = !!card.upgrade;
    const hasVariants = !!card.type_variants;
    variantImg = activeVariant?.image_url || null;
    const imgUrl = variantImg ? variantImg : betaArt && card.beta_image_url ? card.beta_image_url : card.image_url || card.beta_image_url;
    const descText = activeVariant ? activeVariant.description : display.descriptionText;
    const keywordText = activeVariant ? "" : display.keywordText;
    const energyIcon = energyIconMap[card.color] || "colorless";
    const t33 = card.rarity_key || card.rarity;
    let t34;
    if ($[103] !== card.color || $[104] !== t33) {
      t34 = getMerchantPriceRange(t33, card.color);
      $[103] = card.color;
      $[104] = t33;
      $[105] = t34;
    } else {
      t34 = $[105];
    }
    priceRange = t34;
    displayKeywords = [...display.visibleKeywords, ...display.addedKeywords];
    const interactiveWords = buildInteractiveWords(displayKeywords, powerData, keywordData, glossaryData, orbData, bp);
    const spineColor = SPINE_COLOR[card.color] ?? "var(--accent-gold)";
    let t35;
    if ($[106] !== card.color) {
      t35 = card.color ? card.color.charAt(0).toUpperCase() + card.color.slice(1) : "\u2014";
      $[106] = card.color;
      $[107] = t35;
    } else {
      t35 = $[107];
    }
    characterLabel = t35;
    costLabel = card.is_x_cost ? "X" : cost != null && cost < 0 ? "U" : String(cost);
    let t36;
    if ($[108] !== card.target) {
      t36 = card.target && card.target !== "None" && card.target !== "Self" ? card.target.replace(/([A-Z])/g, " $1").trim() : null;
      $[108] = card.target;
      $[109] = t36;
    } else {
      t36 = $[109];
    }
    targetLabel = t36;
    let t37;
    if ($[110] !== cardEnchantments || $[111] !== selectedEnch) {
      t37 = selectedEnch !== "none" && cardEnchantments.includes(selectedEnch);
      $[110] = cardEnchantments;
      $[111] = selectedEnch;
      $[112] = t37;
    } else {
      t37 = $[112];
    }
    enchActive = t37;
    renderSrc = enchActive ? enchantedCardUrl(card.id.toLowerCase(), selectedEnch, isUpgraded, channel, lang) : betaArt || variantImg || cardImgFailed ? imageUrl(imgUrl) : fullCardUrl(card.id.toLowerCase(), isUpgraded, channel, lang);
    let t38;
    if ($[113] !== t) {
      t38 = t("Community");
      $[113] = t;
      $[114] = t38;
    } else {
      t38 = $[114];
    }
    let t39;
    if ($[115] !== t38) {
      t39 = {
        id: "performance",
        label: t38
      };
      $[115] = t38;
      $[116] = t39;
    } else {
      t39 = $[116];
    }
    let t40;
    if ($[117] !== t) {
      t40 = t("Description");
      $[117] = t;
      $[118] = t40;
    } else {
      t40 = $[118];
    }
    let t41;
    if ($[119] !== t40) {
      t41 = {
        id: "description",
        label: t40
      };
      $[119] = t40;
      $[120] = t41;
    } else {
      t41 = $[120];
    }
    let t42;
    if ($[121] !== t) {
      t42 = t("Relations");
      $[121] = t;
      $[122] = t42;
    } else {
      t42 = $[122];
    }
    let t43;
    if ($[123] !== t42) {
      t43 = {
        id: "relations",
        label: t42
      };
      $[123] = t42;
      $[124] = t43;
    } else {
      t43 = $[124];
    }
    let t44;
    if ($[125] !== t) {
      t44 = t("Synergy");
      $[125] = t;
      $[126] = t44;
    } else {
      t44 = $[126];
    }
    let t45;
    if ($[127] !== t44) {
      t45 = {
        id: "pairings",
        label: t44
      };
      $[127] = t44;
      $[128] = t45;
    } else {
      t45 = $[128];
    }
    let t46;
    if ($[129] !== t) {
      t46 = t("Version history");
      $[129] = t;
      $[130] = t46;
    } else {
      t46 = $[130];
    }
    let t47;
    if ($[131] !== t46) {
      t47 = {
        id: "history",
        label: t46
      };
      $[131] = t46;
      $[132] = t47;
    } else {
      t47 = $[132];
    }
    let t48;
    if ($[133] !== t39 || $[134] !== t41 || $[135] !== t43 || $[136] !== t45 || $[137] !== t47) {
      t48 = [t39, t41, t43, t45, t47];
      $[133] = t39;
      $[134] = t41;
      $[135] = t43;
      $[136] = t45;
      $[137] = t47;
      $[138] = t48;
    } else {
      t48 = $[138];
    }
    const tocItems = t48;
    t30 = "card-rvmp";
    let t49;
    if ($[139] !== imgUrl) {
      t49 = imgUrl ? {
        "--entity-bg": `url("${imageUrl(imgUrl)}?bg")`
      } : {};
      $[139] = imgUrl;
      $[140] = t49;
    } else {
      t49 = $[140];
    }
    let t50;
    if ($[141] !== spineColor || $[142] !== t49) {
      t50 = {
        "--spine": spineColor,
        ...t49
      };
      $[141] = spineColor;
      $[142] = t49;
      $[143] = t50;
    } else {
      t50 = $[143];
    }
    t31 = t50 as CSSProperties;
    const t51 = `${bp}/cards`;
    let t52;
    if ($[144] !== t) {
      t52 = t("Back to");
      $[144] = t;
      $[145] = t52;
    } else {
      t52 = $[145];
    }
    let t53;
    if ($[146] !== t) {
      t53 = t("Cards");
      $[146] = t;
      $[147] = t53;
    } else {
      t53 = $[147];
    }
    let t54;
    if ($[148] !== t51 || $[149] !== t52 || $[150] !== t53) {
      t54 = <Link href={t51} className="cd-back">← {t52} {t53}</Link>;
      $[148] = t51;
      $[149] = t52;
      $[150] = t53;
      $[151] = t54;
    } else {
      t54 = $[151];
    }
    let t55;
    if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = {
        marginTop: 12
      };
      $[152] = t55;
    } else {
      t55 = $[152];
    }
    let t56;
    if ($[153] !== card.id) {
      t56 = <div style={t55}><BetaDiffNotice entityType="cards" entityId={card.id} /></div>;
      $[153] = card.id;
      $[154] = t56;
    } else {
      t56 = $[154];
    }
    if ($[155] !== t54 || $[156] !== t56) {
      t32 = <div className="cd-top">{t54}{t56}</div>;
      $[155] = t54;
      $[156] = t56;
      $[157] = t32;
    } else {
      t32 = $[157];
    }
    t29 = "wrap";
    t25 = "main";
    let t57;
    if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <span className="dot">◆</span>;
      $[158] = t57;
    } else {
      t57 = $[158];
    }
    let t58;
    if ($[159] !== characterLabel) {
      t58 = <span>{characterLabel}</span>;
      $[159] = characterLabel;
      $[160] = t58;
    } else {
      t58 = $[160];
    }
    let t59;
    if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
      t59 = <span>·</span>;
      $[161] = t59;
    } else {
      t59 = $[161];
    }
    let t60;
    if ($[162] !== card.rarity) {
      t60 = <span>{card.rarity}</span>;
      $[162] = card.rarity;
      $[163] = t60;
    } else {
      t60 = $[163];
    }
    let t61;
    if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
      t61 = <span>·</span>;
      $[164] = t61;
    } else {
      t61 = $[164];
    }
    const t62 = typeIcons[displayType] || "";
    let t63;
    if ($[165] !== displayType || $[166] !== t62) {
      t63 = <span>{t62} {displayType}</span>;
      $[165] = displayType;
      $[166] = t62;
      $[167] = t63;
    } else {
      t63 = $[167];
    }
    let t64;
    if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
      t64 = <span>·</span>;
      $[168] = t64;
    } else {
      t64 = $[168];
    }
    let t65;
    if ($[169] !== t) {
      t65 = t("Energy");
      $[169] = t;
      $[170] = t65;
    } else {
      t65 = $[170];
    }
    let t66;
    if ($[171] !== costLabel || $[172] !== t65) {
      t66 = <span>{costLabel} {t65}</span>;
      $[171] = costLabel;
      $[172] = t65;
      $[173] = t66;
    } else {
      t66 = $[173];
    }
    let t67;
    if ($[174] !== card.is_x_star_cost || $[175] !== card.star_cost) {
      t67 = (card.star_cost != null || card.is_x_star_cost) && <><span>·</span><span>{card.is_x_star_cost ? "X" : card.star_cost} ★</span></>;
      $[174] = card.is_x_star_cost;
      $[175] = card.star_cost;
      $[176] = t67;
    } else {
      t67 = $[176];
    }
    let t68;
    if ($[177] !== t58 || $[178] !== t60 || $[179] !== t63 || $[180] !== t66 || $[181] !== t67) {
      t68 = <p className="eyebrow">{t57}{t58}{t59}{t60}{t61}{t63}{t64}{t66}{t67}</p>;
      $[177] = t58;
      $[178] = t60;
      $[179] = t63;
      $[180] = t66;
      $[181] = t67;
      $[182] = t68;
    } else {
      t68 = $[182];
    }
    let t69;
    if ($[183] !== hasUpgrade || $[184] !== t || $[185] !== upgraded) {
      t69 = hasUpgrade && <div className="seg hero-seg" role="group" aria-label={t("Card version")}><button type="button" className={`segbtn${!upgraded ? " on" : ""}`} onClick={() => setUpgraded(false)}>{t("Normal")}</button><button type="button" className={`segbtn${upgraded ? " on" : ""}`} onClick={() => setUpgraded(true)}>{t("Upgraded")}</button></div>;
      $[183] = hasUpgrade;
      $[184] = t;
      $[185] = upgraded;
      $[186] = t69;
    } else {
      t69 = $[186];
    }
    const t70 = <div className="hero-head"><h1>{card.name}{isUpgraded && <span className="up">+</span>}</h1>{t69}</div>;
    let t71;
    if ($[187] !== card || $[188] !== upgraded) {
      t71 = <EntityProse kind="card" card={card} upgraded={upgraded} lead={true} />;
      $[187] = card;
      $[188] = upgraded;
      $[189] = t71;
    } else {
      t71 = $[189];
    }
    if ($[190] !== t68 || $[191] !== t70 || $[192] !== t71) {
      t26 = <div className="hero">{t68}{t70}{t71}</div>;
      $[190] = t68;
      $[191] = t70;
      $[192] = t71;
      $[193] = t26;
    } else {
      t26 = $[193];
    }
    let t72;
    if ($[194] !== t) {
      t72 = t("On this page");
      $[194] = t;
      $[195] = t72;
    } else {
      t72 = $[195];
    }
    let t73;
    if ($[196] !== t) {
      t73 = t("On this page");
      $[196] = t;
      $[197] = t73;
    } else {
      t73 = $[197];
    }
    let t74;
    if ($[198] !== t73) {
      t74 = <span className="toc-label">{t73}</span>;
      $[198] = t73;
      $[199] = t74;
    } else {
      t74 = $[199];
    }
    let t75;
    if ($[200] !== activeSection || $[201] !== tocItems) {
      t75 = tocItems.map(it => <a key={it.id} href={`#${it.id}`} className={activeSection === it.id ? "on" : undefined} onClick={e_2 => handleTocClick(e_2, it.id)}>{it.label}</a>);
      $[200] = activeSection;
      $[201] = tocItems;
      $[202] = t75;
    } else {
      t75 = $[202];
    }
    if ($[203] !== t72 || $[204] !== t74 || $[205] !== t75) {
      t27 = <nav className="toc" aria-label={t72}>{t74}{t75}</nav>;
      $[203] = t72;
      $[204] = t74;
      $[205] = t75;
      $[206] = t27;
    } else {
      t27 = $[206];
    }
    let t76;
    if ($[207] !== t) {
      t76 = t("Community performance");
      $[207] = t;
      $[208] = t76;
    } else {
      t76 = $[208];
    }
    let t77;
    if ($[209] !== t76) {
      t77 = <h2>{t76}</h2>;
      $[209] = t76;
      $[210] = t77;
    } else {
      t77 = $[210];
    }
    let t78;
    if ($[211] !== t) {
      t78 = t("Live aggregate across community-submitted runs. Filter by bracket to see how it holds up at higher levels of play.");
      $[211] = t;
      $[212] = t78;
    } else {
      t78 = $[212];
    }
    let t79;
    if ($[213] !== t78) {
      t79 = <p className="h-note">{t78}</p>;
      $[213] = t78;
      $[214] = t79;
    } else {
      t79 = $[214];
    }
    let t80;
    if ($[215] !== card.name || $[216] !== id || $[217] !== initialStats || $[218] !== statsBracket) {
      t80 = <EntityRunStats entityType="cards" entityId={id} entityName={card.name} variant="wiki" initialStats={initialStats} bracket={statsBracket} onBracketChange={setStatsBracket} />;
      $[215] = card.name;
      $[216] = id;
      $[217] = initialStats;
      $[218] = statsBracket;
      $[219] = t80;
    } else {
      t80 = $[219];
    }
    if ($[220] !== t77 || $[221] !== t79 || $[222] !== t80) {
      t28 = <section id="performance">{t77}{t79}{t80}</section>;
      $[220] = t77;
      $[221] = t79;
      $[222] = t80;
      $[223] = t28;
    } else {
      t28 = $[223];
    }
    t19 = "description";
    let t81;
    if ($[224] !== t) {
      t81 = t("Description");
      $[224] = t;
      $[225] = t81;
    } else {
      t81 = $[225];
    }
    if ($[226] !== t81) {
      t20 = <h2>{t81}</h2>;
      $[226] = t81;
      $[227] = t20;
    } else {
      t20 = $[227];
    }
    if ($[228] !== card.name || $[229] !== summary) {
      t21 = <EntitySummary name={card.name} data={summary} />;
      $[228] = card.name;
      $[229] = summary;
      $[230] = t21;
    } else {
      t21 = $[230];
    }
    if ($[231] !== card.type || $[232] !== card.type_variants || $[233] !== hasVariants || $[234] !== selectedVariant) {
      t22 = hasVariants && card.type_variants && <div className="vtoggle">{Object.entries(card.type_variants).map(t82 => {
          const [key, v] = t82;
          const on = selectedVariant === key || !selectedVariant && key === card.type.toLowerCase();
          return <button key={key} type="button" className={`vbtn${on ? " on" : ""}`} onClick={() => setSelectedVariant(selectedVariant === key ? null : key)}>{typeIcons[v.type] || ""} {v.type}</button>;
        })}</div>;
      $[231] = card.type;
      $[232] = card.type_variants;
      $[233] = hasVariants;
      $[234] = selectedVariant;
      $[235] = t22;
    } else {
      t22 = $[235];
    }
    t23 = hasVariants && card.type_variants ? <div className="space-y-3">{Object.entries(card.type_variants).map(t82 => {
        const [key_0, v_0] = t82;
        const isActive = selectedVariant === key_0 || !selectedVariant && key_0 === card.type.toLowerCase();
        return <div key={key_0} className={`text-sm leading-relaxed rounded-lg border transition-colors overflow-hidden ${isActive ? "border-[var(--accent-gold)]/30" : "border-[var(--border-subtle)] opacity-60"}`}><div className={`px-3 py-2 ${isActive ? "bg-[var(--accent-gold)]/5" : "bg-[var(--bg-primary)]/50"}`}><span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mr-2">{typeIcons[v_0.type] || ""} {v_0.type}</span>{v_0.description ? <span className="text-[var(--text-secondary)]"><RichDescription text={v_0.description} energyIcon={energyIcon} /></span> : null}</div>{v_0.riders && v_0.riders.length > 0 && <div className="border-t border-[var(--border-subtle)] px-3 py-2 space-y-1.5">{v_0.riders.map(r => <div key={r.id} className="flex items-start gap-2 text-xs"><span className="font-medium text-[var(--accent-gold)] whitespace-nowrap flex-shrink-0">+ {r.name}</span><span className="text-[var(--text-secondary)]"><RichDescription text={r.description} energyIcon={energyIcon} /></span></div>)}</div>}</div>;
      })}</div> : <><div className="desc-quote"><RichDescription text={descText} energyIcon={energyIcon} relatedCards={spawnedCards.map(_temp3)} interactiveWords={interactiveWords} /></div>{keywordText && <div className="desc-body"><RichDescription text={keywordText} energyIcon={energyIcon} interactiveWords={interactiveWords} /></div>}</>;
    t24 = displayKeywords.length > 0 && <div className="kw-row">{displayKeywords.map(kw => {
        const data_0 = keywordData[kw.toLowerCase()];
        const tip = data_0?.description || keywordTooltips[kw] || "";
        return <HoverTooltip key={kw} title={data_0?.name || kw} content={tip}><Link href={`${bp}/keywords/${(data_0?.id || kw).toLowerCase()}`} className="kw">{data_0?.name || kw}</Link></HoverTooltip>;
      })}</div>;
    $[58] = activeSection;
    $[59] = betaArt;
    $[60] = bp;
    $[61] = card;
    $[62] = cardEnchantments;
    $[63] = cardImgFailed;
    $[64] = channel;
    $[65] = glossaryData;
    $[66] = id;
    $[67] = initialStats;
    $[68] = keywordData;
    $[69] = lang;
    $[70] = orbData;
    $[71] = powerData;
    $[72] = selectedEnch;
    $[73] = selectedVariant;
    $[74] = spawnedCards;
    $[75] = statsBracket;
    $[76] = summary;
    $[77] = t;
    $[78] = upgraded;
    $[79] = characterLabel;
    $[80] = costLabel;
    $[81] = displayKeywords;
    $[82] = displayType;
    $[83] = enchActive;
    $[84] = isUpgraded;
    $[85] = priceRange;
    $[86] = renderSrc;
    $[87] = t19;
    $[88] = t20;
    $[89] = t21;
    $[90] = t22;
    $[91] = t23;
    $[92] = t24;
    $[93] = t25;
    $[94] = t26;
    $[95] = t27;
    $[96] = t28;
    $[97] = t29;
    $[98] = t30;
    $[99] = t31;
    $[100] = t32;
    $[101] = targetLabel;
    $[102] = variantImg;
  } else {
    characterLabel = $[79];
    costLabel = $[80];
    displayKeywords = $[81];
    displayType = $[82];
    enchActive = $[83];
    isUpgraded = $[84];
    priceRange = $[85];
    renderSrc = $[86];
    t19 = $[87];
    t20 = $[88];
    t21 = $[89];
    t22 = $[90];
    t23 = $[91];
    t24 = $[92];
    t25 = $[93];
    t26 = $[94];
    t27 = $[95];
    t28 = $[96];
    t29 = $[97];
    t30 = $[98];
    t31 = $[99];
    t32 = $[100];
    targetLabel = $[101];
    variantImg = $[102];
  }
  let t33;
  if ($[236] !== bp || $[237] !== card.sources || $[238] !== t) {
    t33 = card.sources && card.sources.length > 0 && <><h3 className="subh">{t("Sources")}</h3><p className="h-note">{t("Added to your deck in combat by:")}</p><div className="kw-row">{card.sources.map(s_0 => <Link key={s_0.id} href={`${bp}/monsters/${s_0.id.toLowerCase()}`} className="kw">{s_0.name}</Link>)}</div></>;
    $[236] = bp;
    $[237] = card.sources;
    $[238] = t;
    $[239] = t33;
  } else {
    t33 = $[239];
  }
  let t34;
  if ($[240] !== bp || $[241] !== card.powers_applied || $[242] !== powerData || $[243] !== t) {
    t34 = card.powers_applied && card.powers_applied.length > 0 && <><h3 className="subh">{t("Powers Applied")}</h3><div className="pow-list">{card.powers_applied.map(pa => {
          const powerName = pa.power_key || pa.power;
          const powerId = powerName.replace(/([A-Z])/g, "_$1").replace(/^_/, "").toUpperCase();
          const prettyName = pa.power.replace(/([A-Z])/g, " $1").trim();
          const data_1 = powerData[pa.power.toLowerCase()];
          return <HoverTooltip key={pa.power} title={prettyName} content={data_1?.description}><Link href={`${bp}/powers/${powerId}`}>{prettyName}{pa.amount ? ` ${pa.amount}` : ""}</Link></HoverTooltip>;
        })}</div></>;
    $[240] = bp;
    $[241] = card.powers_applied;
    $[242] = powerData;
    $[243] = t;
    $[244] = t34;
  } else {
    t34 = $[244];
  }
  let t35;
  if ($[245] !== t19 || $[246] !== t20 || $[247] !== t21 || $[248] !== t22 || $[249] !== t23 || $[250] !== t24 || $[251] !== t33 || $[252] !== t34) {
    t35 = <section id={t19}>{t20}{t21}{t22}{t23}{t24}{t33}{t34}</section>;
    $[245] = t19;
    $[246] = t20;
    $[247] = t21;
    $[248] = t22;
    $[249] = t23;
    $[250] = t24;
    $[251] = t33;
    $[252] = t34;
    $[253] = t35;
  } else {
    t35 = $[253];
  }
  let t36;
  if ($[254] !== t) {
    t36 = t("Relations");
    $[254] = t;
    $[255] = t36;
  } else {
    t36 = $[255];
  }
  let t37;
  if ($[256] !== t36) {
    t37 = <h2>{t36}</h2>;
    $[256] = t36;
    $[257] = t37;
  } else {
    t37 = $[257];
  }
  let t38;
  if ($[258] !== t) {
    t38 = t("What this card makes, and what else interacts with it.");
    $[258] = t;
    $[259] = t38;
  } else {
    t38 = $[259];
  }
  let t39;
  if ($[260] !== t38) {
    t39 = <p className="h-note">{t38}</p>;
    $[260] = t38;
    $[261] = t39;
  } else {
    t39 = $[261];
  }
  let t40;
  if ($[262] !== bp || $[263] !== spawnedCards || $[264] !== t) {
    t40 = spawnedCards.length > 0 && <div className="rel-block"><div className="rl">{t("Generates")}</div><div className="chips">{spawnedCards.map(sc_0 => <Link key={sc_0.id} href={`${bp}/cards/${sc_0.id.toLowerCase()}`} className="cardlink">{sc_0.image_url && <img className="cardimg xs" src={imageUrl(sc_0.image_url)} alt="" crossOrigin="anonymous" />}<span><span className="cln">{sc_0.name}</span><span className="cls">{sc_0.type}</span></span></Link>)}</div></div>;
    $[262] = bp;
    $[263] = spawnedCards;
    $[264] = t;
    $[265] = t40;
  } else {
    t40 = $[265];
  }
  let t41;
  if ($[266] !== card.color || $[267] !== card.tags || $[268] !== displayKeywords || $[269] !== id || $[270] !== initialRelated) {
    t41 = <RelatedCards currentId={id} keywords={displayKeywords} tags={card.tags} color={card.color} initialGroups={initialRelated} />;
    $[266] = card.color;
    $[267] = card.tags;
    $[268] = displayKeywords;
    $[269] = id;
    $[270] = initialRelated;
    $[271] = t41;
  } else {
    t41 = $[271];
  }
  let t42;
  if ($[272] !== t40 || $[273] !== t41) {
    t42 = <div className="rel">{t40}{t41}</div>;
    $[272] = t40;
    $[273] = t41;
    $[274] = t42;
  } else {
    t42 = $[274];
  }
  let t43;
  if ($[275] !== t37 || $[276] !== t39 || $[277] !== t42) {
    t43 = <section id="relations">{t37}{t39}{t42}</section>;
    $[275] = t37;
    $[276] = t39;
    $[277] = t42;
    $[278] = t43;
  } else {
    t43 = $[278];
  }
  let t44;
  if ($[279] !== bp || $[280] !== card.name || $[281] !== id || $[282] !== initialPairings || $[283] !== lang) {
    t44 = <EntityPairings kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialPairings} />;
    $[279] = bp;
    $[280] = card.name;
    $[281] = id;
    $[282] = initialPairings;
    $[283] = lang;
    $[284] = t44;
  } else {
    t44 = $[284];
  }
  let t45;
  if ($[285] !== bp || $[286] !== card.name || $[287] !== id || $[288] !== initialRecs || $[289] !== lang) {
    t45 = <EntityDraftRecs kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialRecs} />;
    $[285] = bp;
    $[286] = card.name;
    $[287] = id;
    $[288] = initialRecs;
    $[289] = lang;
    $[290] = t45;
  } else {
    t45 = $[290];
  }
  let t46;
  let t47;
  if ($[291] !== id) {
    t46 = <LocalizedNames entityType="cards" entityId={id} />;
    t47 = <EntityUpdateHistory entityType="cards" entityId={id} />;
    $[291] = id;
    $[292] = t46;
    $[293] = t47;
  } else {
    t46 = $[292];
    t47 = $[293];
  }
  let t48;
  if ($[294] !== t25 || $[295] !== t26 || $[296] !== t27 || $[297] !== t28 || $[298] !== t35 || $[299] !== t43 || $[300] !== t44 || $[301] !== t45 || $[302] !== t46 || $[303] !== t47) {
    t48 = <main className={t25}>{t26}{t27}{t28}{t35}{t43}{t44}{t45}{t46}{t47}</main>;
    $[294] = t25;
    $[295] = t26;
    $[296] = t27;
    $[297] = t28;
    $[298] = t35;
    $[299] = t43;
    $[300] = t44;
    $[301] = t45;
    $[302] = t46;
    $[303] = t47;
    $[304] = t48;
  } else {
    t48 = $[304];
  }
  const t49 = `${card.name}${isUpgraded ? "+" : ""}`;
  let t50;
  if ($[305] !== t || $[306] !== t49) {
    t50 = t("{name} - Slay the Spire 2", {
      name: t49
    });
    $[305] = t;
    $[306] = t49;
    $[307] = t50;
  } else {
    t50 = $[307];
  }
  let t51;
  if ($[308] !== betaArt || $[309] !== card.id || $[310] !== channel || $[311] !== enchActive || $[312] !== lang || $[313] !== selectedEnch || $[314] !== variantImg) {
    t51 = e_3 => {
      const el_0 = e_3.currentTarget;
      if (enchActive) {
        if (!el_0.dataset.fb) {
          el_0.dataset.fb = "1";
          el_0.src = enchantedCardUrl(card.id.toLowerCase(), selectedEnch, false, channel, lang);
        }
      } else {
        if (!betaArt && !variantImg) {
          setCardImgFailed(true);
        }
      }
    };
    $[308] = betaArt;
    $[309] = card.id;
    $[310] = channel;
    $[311] = enchActive;
    $[312] = lang;
    $[313] = selectedEnch;
    $[314] = variantImg;
    $[315] = t51;
  } else {
    t51 = $[315];
  }
  let t52;
  if ($[316] !== renderSrc || $[317] !== t50 || $[318] !== t51) {
    t52 = <img key={renderSrc} className="cardimg render cardframe" src={renderSrc} alt={t50} crossOrigin="anonymous" onError={t51} />;
    $[316] = renderSrc;
    $[317] = t50;
    $[318] = t51;
    $[319] = t52;
  } else {
    t52 = $[319];
  }
  let t53;
  if ($[320] !== cardEnchantments || $[321] !== enchMeta || $[322] !== selectedEnch || $[323] !== t) {
    t53 = cardEnchantments.length > 0 && <select className="ench-select" aria-label={t("Enchantment")} value={selectedEnch} onChange={e_4 => setSelectedEnch(e_4.target.value)}><option value="none">{t("No enchantment")}</option>{cardEnchantments.map(eid => <option key={eid} value={eid}>{enchMeta[eid]?.name ?? eid}</option>)}</select>;
    $[320] = cardEnchantments;
    $[321] = enchMeta;
    $[322] = selectedEnch;
    $[323] = t;
    $[324] = t53;
  } else {
    t53 = $[324];
  }
  let t54;
  if ($[325] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = d => {
      if (d) {
        setCard(d as Card);
      }
    };
    $[325] = t54;
  } else {
    t54 = $[325];
  }
  let t55;
  if ($[326] !== id || $[327] !== statsBracket) {
    t55 = <EntityVersionSelect entityType="cards" entityId={id} bracket={statsBracket} onBracketChange={setStatsBracket} onEntityData={t54} />;
    $[326] = id;
    $[327] = statsBracket;
    $[328] = t55;
  } else {
    t55 = $[328];
  }
  let t56;
  if ($[329] !== t53 || $[330] !== t55) {
    t56 = <div className="variant">{t53}{t55}</div>;
    $[329] = t53;
    $[330] = t55;
    $[331] = t56;
  } else {
    t56 = $[331];
  }
  let t57;
  if ($[332] !== t) {
    t57 = t("At a glance");
    $[332] = t;
    $[333] = t57;
  } else {
    t57 = $[333];
  }
  let t58;
  if ($[334] !== t57) {
    t58 = <div className="fh">{t57}</div>;
    $[334] = t57;
    $[335] = t58;
  } else {
    t58 = $[335];
  }
  let t59;
  if ($[336] !== t) {
    t59 = t("Energy");
    $[336] = t;
    $[337] = t59;
  } else {
    t59 = $[337];
  }
  let t60;
  if ($[338] !== t59) {
    t60 = <dt>{t59}</dt>;
    $[338] = t59;
    $[339] = t60;
  } else {
    t60 = $[339];
  }
  let t61;
  if ($[340] !== costLabel) {
    t61 = <dd>{costLabel}</dd>;
    $[340] = costLabel;
    $[341] = t61;
  } else {
    t61 = $[341];
  }
  let t62;
  if ($[342] !== t60 || $[343] !== t61) {
    t62 = <div className="frow">{t60}{t61}</div>;
    $[342] = t60;
    $[343] = t61;
    $[344] = t62;
  } else {
    t62 = $[344];
  }
  let t63;
  if ($[345] !== card.is_x_star_cost || $[346] !== card.star_cost || $[347] !== t) {
    t63 = (card.star_cost != null || card.is_x_star_cost) && <div className="frow"><dt>{t("Star Cost")}</dt><dd>{card.is_x_star_cost ? "X" : card.star_cost}</dd></div>;
    $[345] = card.is_x_star_cost;
    $[346] = card.star_cost;
    $[347] = t;
    $[348] = t63;
  } else {
    t63 = $[348];
  }
  let t64;
  if ($[349] !== t) {
    t64 = t("Type");
    $[349] = t;
    $[350] = t64;
  } else {
    t64 = $[350];
  }
  let t65;
  if ($[351] !== t64) {
    t65 = <dt>{t64}</dt>;
    $[351] = t64;
    $[352] = t65;
  } else {
    t65 = $[352];
  }
  let t66;
  if ($[353] !== displayType) {
    t66 = <dd>{displayType}</dd>;
    $[353] = displayType;
    $[354] = t66;
  } else {
    t66 = $[354];
  }
  let t67;
  if ($[355] !== t65 || $[356] !== t66) {
    t67 = <div className="frow">{t65}{t66}</div>;
    $[355] = t65;
    $[356] = t66;
    $[357] = t67;
  } else {
    t67 = $[357];
  }
  let t68;
  if ($[358] !== t) {
    t68 = t("Rarity");
    $[358] = t;
    $[359] = t68;
  } else {
    t68 = $[359];
  }
  let t69;
  if ($[360] !== t68) {
    t69 = <dt>{t68}</dt>;
    $[360] = t68;
    $[361] = t69;
  } else {
    t69 = $[361];
  }
  let t70;
  if ($[362] !== card.rarity) {
    t70 = <dd>{card.rarity}</dd>;
    $[362] = card.rarity;
    $[363] = t70;
  } else {
    t70 = $[363];
  }
  let t71;
  if ($[364] !== t69 || $[365] !== t70) {
    t71 = <div className="frow">{t69}{t70}</div>;
    $[364] = t69;
    $[365] = t70;
    $[366] = t71;
  } else {
    t71 = $[366];
  }
  let t72;
  if ($[367] !== t) {
    t72 = t("Character");
    $[367] = t;
    $[368] = t72;
  } else {
    t72 = $[368];
  }
  let t73;
  if ($[369] !== t72) {
    t73 = <dt>{t72}</dt>;
    $[369] = t72;
    $[370] = t73;
  } else {
    t73 = $[370];
  }
  let t74;
  if ($[371] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = {
      color: "var(--spine)"
    };
    $[371] = t74;
  } else {
    t74 = $[371];
  }
  let t75;
  if ($[372] !== characterLabel) {
    t75 = <dd style={t74}>{characterLabel}</dd>;
    $[372] = characterLabel;
    $[373] = t75;
  } else {
    t75 = $[373];
  }
  let t76;
  if ($[374] !== t73 || $[375] !== t75) {
    t76 = <div className="frow">{t73}{t75}</div>;
    $[374] = t73;
    $[375] = t75;
    $[376] = t76;
  } else {
    t76 = $[376];
  }
  let t77;
  if ($[377] !== t || $[378] !== targetLabel) {
    t77 = targetLabel && <div className="frow"><dt>{t("Target")}</dt><dd>{targetLabel}</dd></div>;
    $[377] = t;
    $[378] = targetLabel;
    $[379] = t77;
  } else {
    t77 = $[379];
  }
  let t78;
  if ($[380] !== displayKeywords || $[381] !== t) {
    t78 = displayKeywords.length > 0 && <div className="frow"><dt>{t("Keywords")}</dt><dd>{displayKeywords.map(_temp4)}</dd></div>;
    $[380] = displayKeywords;
    $[381] = t;
    $[382] = t78;
  } else {
    t78 = $[382];
  }
  let t79;
  if ($[383] !== bp || $[384] !== spawnedCards || $[385] !== t) {
    t79 = spawnedCards.length > 0 && <div className="frow"><dt>{t("Generates")}</dt><dd>{spawnedCards.map((sc_1, i) => <span key={sc_1.id}>{i > 0 ? ", " : ""}<Link href={`${bp}/cards/${sc_1.id.toLowerCase()}`}>{sc_1.name}</Link></span>)}</dd></div>;
    $[383] = bp;
    $[384] = spawnedCards;
    $[385] = t;
    $[386] = t79;
  } else {
    t79 = $[386];
  }
  let t80;
  if ($[387] !== priceRange || $[388] !== t) {
    t80 = priceRange && <div className="frow"><dt>{t("Merchant Price")}</dt><dd><img src={imageUrl("/static/images/ui/rewards/reward_icon_money.webp")} alt={t("Gold")} style={{
          width: 15,
          height: 15
        }} crossOrigin="anonymous" />{priceRange.min}–{priceRange.max}</dd></div>;
    $[387] = priceRange;
    $[388] = t;
    $[389] = t80;
  } else {
    t80 = $[389];
  }
  let t81;
  if ($[390] !== t62 || $[391] !== t63 || $[392] !== t67 || $[393] !== t71 || $[394] !== t76 || $[395] !== t77 || $[396] !== t78 || $[397] !== t79 || $[398] !== t80) {
    t81 = <dl>{t62}{t63}{t67}{t71}{t76}{t77}{t78}{t79}{t80}</dl>;
    $[390] = t62;
    $[391] = t63;
    $[392] = t67;
    $[393] = t71;
    $[394] = t76;
    $[395] = t77;
    $[396] = t78;
    $[397] = t79;
    $[398] = t80;
    $[399] = t81;
  } else {
    t81 = $[399];
  }
  let t82;
  if ($[400] !== t58 || $[401] !== t81) {
    t82 = <div className="facts">{t58}{t81}</div>;
    $[400] = t58;
    $[401] = t81;
    $[402] = t82;
  } else {
    t82 = $[402];
  }
  let t83;
  bb0: {
    const mini = miniStats?.brackets?.[statsBracket] ?? miniStats;
    if (!mini || mini.picks <= 0) {
      t83 = null;
      break bb0;
    }
    let t84;
    if ($[403] !== t) {
      t84 = t("Community");
      $[403] = t;
      $[404] = t84;
    } else {
      t84 = $[404];
    }
    let t85;
    if ($[405] !== t84) {
      t85 = <div className="mh">{t84}</div>;
      $[405] = t84;
      $[406] = t85;
    } else {
      t85 = $[406];
    }
    const t86 = mini.win_rate >= 50 ? "var(--good)" : "var(--warn)";
    let t87;
    if ($[407] !== t86) {
      t87 = {
        color: t86
      };
      $[407] = t86;
      $[408] = t87;
    } else {
      t87 = $[408];
    }
    let t88;
    if ($[409] !== mini.win_rate || $[410] !== t87) {
      t88 = <div className="mv" style={t87}>{mini.win_rate}%</div>;
      $[409] = mini.win_rate;
      $[410] = t87;
      $[411] = t88;
    } else {
      t88 = $[411];
    }
    let t89;
    if ($[412] !== t) {
      t89 = t("Win rate");
      $[412] = t;
      $[413] = t89;
    } else {
      t89 = $[413];
    }
    let t90;
    if ($[414] !== t89) {
      t90 = <div className="ml">{t89}</div>;
      $[414] = t89;
      $[415] = t90;
    } else {
      t90 = $[415];
    }
    let t91;
    if ($[416] !== t88 || $[417] !== t90) {
      t91 = <div>{t88}{t90}</div>;
      $[416] = t88;
      $[417] = t90;
      $[418] = t91;
    } else {
      t91 = $[418];
    }
    let t92;
    if ($[419] !== mini.pick_rate) {
      t92 = <div className="mv">{mini.pick_rate}%</div>;
      $[419] = mini.pick_rate;
      $[420] = t92;
    } else {
      t92 = $[420];
    }
    let t93;
    if ($[421] !== t) {
      t93 = t("Pick rate");
      $[421] = t;
      $[422] = t93;
    } else {
      t93 = $[422];
    }
    let t94;
    if ($[423] !== t93) {
      t94 = <div className="ml">{t93}</div>;
      $[423] = t93;
      $[424] = t94;
    } else {
      t94 = $[424];
    }
    let t95;
    if ($[425] !== t92 || $[426] !== t94) {
      t95 = <div>{t92}{t94}</div>;
      $[425] = t92;
      $[426] = t94;
      $[427] = t95;
    } else {
      t95 = $[427];
    }
    let t96;
    if ($[428] !== mini.score || $[429] !== t) {
      t96 = mini.score != null && <div><div className="mv">{mini.score}</div><div className="ml">{t("Codex Score")}</div></div>;
      $[428] = mini.score;
      $[429] = t;
      $[430] = t96;
    } else {
      t96 = $[430];
    }
    let t97;
    if ($[431] !== mini.elo || $[432] !== t) {
      t97 = mini.elo != null && <div><div className="mv">{Math.round(mini.elo)}</div><div className="ml">{t("Elo")}</div></div>;
      $[431] = mini.elo;
      $[432] = t;
      $[433] = t97;
    } else {
      t97 = $[433];
    }
    let t98;
    if ($[434] !== t91 || $[435] !== t95 || $[436] !== t96 || $[437] !== t97) {
      t98 = <div className="mg">{t91}{t95}{t96}{t97}</div>;
      $[434] = t91;
      $[435] = t95;
      $[436] = t96;
      $[437] = t97;
      $[438] = t98;
    } else {
      t98 = $[438];
    }
    let t99;
    if ($[439] !== t85 || $[440] !== t98) {
      t99 = <div className="mini">{t85}{t98}</div>;
      $[439] = t85;
      $[440] = t98;
      $[441] = t99;
    } else {
      t99 = $[441];
    }
    t83 = t99;
  }
  let t84;
  if ($[442] !== t52 || $[443] !== t56 || $[444] !== t82 || $[445] !== t83) {
    t84 = <aside className="aside"><div className="box">{t52}{t56}{t82}{t83}</div></aside>;
    $[442] = t52;
    $[443] = t56;
    $[444] = t82;
    $[445] = t83;
    $[446] = t84;
  } else {
    t84 = $[446];
  }
  let t85;
  if ($[447] !== t29 || $[448] !== t48 || $[449] !== t84) {
    t85 = <div className={t29}>{t48}{t84}</div>;
    $[447] = t29;
    $[448] = t48;
    $[449] = t84;
    $[450] = t85;
  } else {
    t85 = $[450];
  }
  let t86;
  if ($[451] !== t30 || $[452] !== t31 || $[453] !== t32 || $[454] !== t85) {
    t86 = <div className={t30} style={t31}>{t32}{t85}</div>;
    $[451] = t30;
    $[452] = t31;
    $[453] = t32;
    $[454] = t85;
    $[455] = t86;
  } else {
    t86 = $[455];
  }
  return t86;
}
function _temp4(kw_0) {
  return <span className="kw" key={kw_0}>{kw_0}</span>;
}
function _temp3(sc) {
  return {
    id: sc.id,
    name: sc.name,
    image_url: sc.image_url,
    type: sc.type,
    rarity: sc.rarity,
    cost: sc.cost
  };
}
function _temp2() {}
function _temp() {
  return null;
}